import { AlertTriangleIcon, AlertWarningIcon, SelectOption } from '@spoiler-alert/ui-library';
import accounting from 'accounting';
import React from 'react';
import { v4 as uuid } from 'uuid';
import { ReservePriceConstants, SHELF_LIFE_BRACKET_OPTIONS } from './reserve-price-constants';

interface CSSClasses {
  [key: string]: string;
}

interface RPError extends Error {
  code: string;
}

interface Bracket {
  _id?: string;
  min?: number | null;
  max?: number | null;
  multiplier: number | undefined;
  selectedBracketRange?: {
    value: string;
    text: string;
  }[];
}

interface Exception {
  scope?: {
    scopeBasis: string | undefined;
    scopeValues: string[];
  };
  shelfLifeBrackets: Bracket[];
}

interface Setting {
  primaryAnchor: string | undefined;
  shelfLifeBrackets: Bracket[];
  exceptions?: Exception[];
}

interface RPOption {
  _id: string;
  value: string;
  category: string;
}

interface BracketRaw {
  _id: string;
  min?: string;
  max?: string;
  multiplier: string;
}

interface ExceptionRaw {
  scope: {
    scopeBasis: string;
    scopeValues: string | RPOption[];
  };
  shelfLifeBrackets: BracketRaw[];
}

interface SettingRaw {
  primaryAnchor: string;
  shelfLifeBrackets: BracketRaw[];
  exceptions: ExceptionRaw[];
  multiplier?: string;
}

const getDefaultBracket = () => ({
  _id: uuid(),
  multiplier: 0,
  selectedBracketRange: SHELF_LIFE_BRACKET_OPTIONS.ALL_INVENTORY,
});

const renderTimelineError = (error: 'gaps' | 'overlaps' | 'closedend', className: string): React.ReactNode => {
  let errorMessage = '';
  if (!error || error.length === 0) return null;
  if (error === 'gaps') errorMessage = 'Shelf life brackets cannot have gaps.';
  if (error === 'overlaps') errorMessage = 'Shelf life brackets cannot have overlaps.';
  if (error === 'closedend') errorMessage = 'All shelf life must be accounted for.';
  return (
    <div className={className}>
      <AlertTriangleIcon /> {errorMessage}
    </div>
  );
};

const renderNoShelfLifeInfo = (hasShelfLifeInfo: boolean, primaryAnchor: string, classes: CSSClasses) => {
  if (hasShelfLifeInfo || !primaryAnchor) return null;
  return (
    <div className={classes.stepErrorContainer}>
      <div className={classes.stepInfo}>
        <AlertWarningIcon className={classes.alertWarning} />
        <span>{ReservePriceConstants.noShelfLife}</span>
      </div>
    </div>
  );
};

const renderErrorMessages = (errors: RPError[], classes: CSSClasses) => {
  if (!errors || !errors.length) return null;
  return (
    <div className={classes.stepErrorContainer}>
      {errors.map((error, i) => (
        <div className={classes.stepErrorInfo} key={error.code + i}>
          <AlertTriangleIcon className={classes.alertTriangle} />
          <span>{error.message}</span>
        </div>
      ))}
    </div>
  );
};

const getBracketRange = (bracket: Bracket) => {
  if (!bracket.min && !bracket.max) {
    return SHELF_LIFE_BRACKET_OPTIONS.ALL_INVENTORY;
  }
  if (bracket.min && bracket.max) {
    return SHELF_LIFE_BRACKET_OPTIONS.BETWEEN;
  }
  if (!bracket.min && bracket.max) {
    return SHELF_LIFE_BRACKET_OPTIONS.LESS;
  }
  if (bracket.min && !bracket.max) {
    return SHELF_LIFE_BRACKET_OPTIONS.GREATER;
  }
  return undefined;
};

const normalizeBracketForDisplay = (b: Bracket) => {
  const copy = { ...b };
  if (copy.min === 0 || copy.min === null) delete copy.min;
  if (copy.max === 0 || copy.max === null) delete copy.max;
  if (copy.multiplier === null) copy.multiplier = 0;
  return {
    ...copy,
    selectedBracketRange: !b.selectedBracketRange ? getBracketRange(b) : b.selectedBracketRange,
  };
};

const cleanBracketsForMutation = (brackets: BracketRaw[]) =>
  (brackets || []).map((b) => {
    const cleaned: Bracket = {
      min: b.min ? accounting.unformat(b.min) : undefined,
      max: b.max ? accounting.unformat(b.max) : undefined,
      multiplier: accounting.unformat(b.multiplier),
    };
    if (cleaned.min === undefined) delete cleaned.min;
    if (cleaned.max === undefined) delete cleaned.max;
    return cleaned;
  });

const cleanExceptionsForMutation = (exceptions: ExceptionRaw[]): Exception[] => {
  return (exceptions || []).map((ex) => ({
    scope: ex.scope
      ? {
          scopeBasis: ex.scope.scopeBasis ? String(ex.scope.scopeBasis) : undefined,
          scopeValues: Array.isArray(ex.scope.scopeValues) ? ex.scope.scopeValues.map((v) => (typeof v === 'object' ? v.value : v)) : [],
        }
      : undefined,
    shelfLifeBrackets: cleanBracketsForMutation(ex.shelfLifeBrackets),
  }));
};

const cleanReservePriceSettingsForMutation = (input: SettingRaw): Setting => {
  if (input.primaryAnchor === 'SetOnUpload') {
    return {
      primaryAnchor: input.primaryAnchor,
      shelfLifeBrackets: [
        {
          min: null,
          max: null,
          multiplier: input.multiplier ? accounting.unformat(input.multiplier) : undefined,
        },
      ],
    };
  }
  return {
    primaryAnchor: input.primaryAnchor || undefined,
    shelfLifeBrackets: cleanBracketsForMutation(input.shelfLifeBrackets),
    exceptions: cleanExceptionsForMutation(input.exceptions),
  };
};

const renderExceptionParameterSelectOptions = (validOptions: { id: string; name: string }[]) =>
  validOptions?.map((opt) => (
    <SelectOption key={opt.id} value={opt.id}>
      {opt.name}
    </SelectOption>
  ));

const sortOptionsByCategoryAndValue = (a: RPOption, b: RPOption) => {
  if (a.category === b.category) {
    return a.value.localeCompare(b.value);
  }
  return a.category.localeCompare(b.category);
};

const combineClasses = (...args: string[]) => args.filter(Boolean).join(' ');

const isAddExceptionBracketDisabled = (exception: ExceptionRaw) => {
  return !exception.scope?.scopeValues || exception.scope.scopeValues.length === 0;
};

export {
  cleanBracketsForMutation,
  cleanExceptionsForMutation,
  cleanReservePriceSettingsForMutation,
  combineClasses,
  getBracketRange,
  getDefaultBracket,
  isAddExceptionBracketDisabled,
  normalizeBracketForDisplay,
  renderErrorMessages,
  renderExceptionParameterSelectOptions,
  renderNoShelfLifeInfo,
  renderTimelineError,
  sortOptionsByCategoryAndValue,
};
